import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnglesLeft,
  faChevronRight,
  faLinkSlash,
  faPenToSquare,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import Modal from './Modal';
import AddTaskForm from '../pages/AddTask';
import { fetchBoardTasks, updateTask, type TaskRow } from '../api/tasks';
import {
  clearProjectBoardLayoutLegacy,
  createDefaultProjectBoardColumns,
  fetchProjectBoardLayout,
  loadProjectBoardCollapsed,
  loadProjectBoardLayoutLegacy,
  saveProjectBoardCollapsed,
  saveProjectBoardLayout,
  type ProjectBoardColumnDef,
  type ProjectBoardItemOrder,
} from '../api/projectBoard';
import { TAG_PILL_COLORS, ROLE_LABELS as TAG_LABELS } from '../constants/taskTags';
import '../pages/TasksBoard.css';
import './ProjectBoardTab.css';

const PRIORITY_PILL_COLORS: Record<string, string> = {
  Low: '#73BF4380',
  Medium: '#FFC10780',
  High: '#E48B5280',
  Urgent: '#ef444480',
};

export interface ProjectBoardColumn {
  id: string;
  title: string;
  items: TaskRow[];
}

function newId(): string {
  return `pb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

type ItemOrder = ProjectBoardItemOrder;

/** Identity of the shared layout, so a save only fires for changes we made. */
function layoutFingerprint(columnDefs: ProjectBoardColumnDef[], itemOrder: ItemOrder): string {
  return JSON.stringify({
    columns: columnDefs,
    itemOrder: Object.keys(itemOrder)
      .sort()
      .reduce<ItemOrder>((acc, key) => {
        acc[key] = itemOrder[key];
        return acc;
      }, {}),
  });
}

function buildColumns(
  columnDefs: ProjectBoardColumnDef[],
  tasks: TaskRow[],
  itemOrder: ItemOrder
): ProjectBoardColumn[] {
  return columnDefs.map((c) => {
    const colTasks = tasks.filter((t) => t.project_board_column_id === c.id);
    const order = itemOrder[c.id] ?? [];
    const rank = new Map<number, number>();
    order.forEach((id, idx) => rank.set(id, idx));
    const items = [...colTasks].sort((a, b) => {
      const ra = rank.has(a.id) ? (rank.get(a.id) as number) : Infinity;
      const rb = rank.has(b.id) ? (rank.get(b.id) as number) : Infinity;
      if (ra !== rb) return ra - rb;
      // Unranked (new) tasks fall back to most-recently-updated first.
      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
    return { id: c.id, title: c.title, items };
  });
}

function assigneeDisplay(task: TaskRow): string {
  if (task.assignees && task.assignees.length > 0) {
    return task.assignees.map((a) => a.name).join(', ');
  }
  if (task.assigned_to_name?.trim()) return task.assigned_to_name;
  return '';
}

interface DragPayload {
  itemId: string;
  fromColumnId: string;
}

interface ProjectBoardTabProps {
  projectId: number;
  /** When set, load tasks by event_id (event board) instead of project_id only. */
  eventId?: number;
  viewerUserId: number;
  canEditBoard?: boolean;
}

const ProjectBoardTab: React.FC<ProjectBoardTabProps> = ({
  projectId,
  eventId,
  viewerUserId,
  canEditBoard = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [columnDefs, setColumnDefs] = useState<ProjectBoardColumnDef[]>(() => createDefaultProjectBoardColumns());
  const [collapsedIds, setCollapsedIds] = useState<string[]>(() => loadProjectBoardCollapsed(projectId));
  const [itemOrder, setItemOrder] = useState<ItemOrder>({});
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [layoutError, setLayoutError] = useState('');
  /** Fingerprint of the layout the server last confirmed, so saves fire only on real edits. */
  const persistedLayoutRef = useRef('');
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState('');
  const [addTaskColumnId, setAddTaskColumnId] = useState<string | null>(null);

  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [editTitleDraft, setEditTitleDraft] = useState('');
  const [deletingColumnId, setDeletingColumnId] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const [removingTaskId, setRemovingTaskId] = useState<number | null>(null);
  const [dragged, setDragged] = useState<DragPayload | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);
  const [dragInsertAfter, setDragInsertAfter] = useState(false);
  const isDraggingRef = useRef(false);
  const draggedRef = useRef<DragPayload | null>(null);
  const dragInsertAfterRef = useRef(false);

  // Column (section) drag-to-reorder state.
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [columnDragOverId, setColumnDragOverId] = useState<string | null>(null);
  const [columnInsertAfter, setColumnInsertAfter] = useState(false);
  const dragKindRef = useRef<'card' | 'column' | null>(null);
  const draggedColumnIdRef = useRef<string | null>(null);
  const columnInsertAfterRef = useRef(false);

  const columns = useMemo(() => buildColumns(columnDefs, tasks, itemOrder), [columnDefs, tasks, itemOrder]);

  const reloadTasks = useCallback(async () => {
    if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) {
      setTasks([]);
      setTasksLoading(false);
      return;
    }
    setTasksLoading(true);
    setTasksError('');
    try {
      const list = await fetchBoardTasks(viewerUserId, { projectId, eventId });
      setTasks(list);
    } catch {
      setTasksError('Failed to load tasks.');
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [projectId, eventId, viewerUserId]);

  useEffect(() => {
    reloadTasks();
  }, [reloadTasks]);

  /**
   * The sections come from the project, so everyone opens the same board. A project
   * that has never published a layout adopts whatever this browser had saved before
   * the move to the server — that is how sections someone already built reach the
   * rest of the team — and otherwise starts from the defaults.
   */
  useEffect(() => {
    let cancelled = false;
    setLayoutLoaded(false);
    setLayoutError('');
    setEditingColumnId(null);
    setCollapsedIds(loadProjectBoardCollapsed(projectId));

    (async () => {
      if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) {
        return;
      }
      try {
        const shared = await fetchProjectBoardLayout(projectId, viewerUserId);
        if (cancelled) return;
        if (shared) {
          setColumnDefs(shared.columns);
          setItemOrder(shared.itemOrder);
          persistedLayoutRef.current = layoutFingerprint(shared.columns, shared.itemOrder);
          setLayoutLoaded(true);
          return;
        }

        const legacy = loadProjectBoardLayoutLegacy(projectId);
        const seed = legacy ?? { columns: createDefaultProjectBoardColumns(), itemOrder: {} };
        setColumnDefs(seed.columns);
        setItemOrder(seed.itemOrder);
        if (!canEditBoard) {
          persistedLayoutRef.current = layoutFingerprint(seed.columns, seed.itemOrder);
          setLayoutLoaded(true);
          return;
        }
        try {
          const saved = await saveProjectBoardLayout(projectId, viewerUserId, seed);
          if (cancelled) return;
          setColumnDefs(saved.columns);
          setItemOrder(saved.itemOrder);
          persistedLayoutRef.current = layoutFingerprint(saved.columns, saved.itemOrder);
          clearProjectBoardLayoutLegacy(projectId);
        } catch {
          /* Publishing can wait; the next edit tries again. */
          persistedLayoutRef.current = '';
        }
        if (!cancelled) setLayoutLoaded(true);
      } catch {
        if (cancelled) return;
        setLayoutError('Could not load this board’s sections. Reload to try again.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, viewerUserId, canEditBoard]);

  /** Publish section edits for everyone. Skipped until the shared layout has loaded. */
  useEffect(() => {
    if (!layoutLoaded || !canEditBoard) return;
    if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) return;
    const fingerprint = layoutFingerprint(columnDefs, itemOrder);
    if (fingerprint === persistedLayoutRef.current) return;
    let cancelled = false;
    saveProjectBoardLayout(projectId, viewerUserId, { columns: columnDefs, itemOrder })
      .then(() => {
        if (cancelled) return;
        persistedLayoutRef.current = fingerprint;
        setLayoutError('');
        clearProjectBoardLayoutLegacy(projectId);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to save the board layout';
        setLayoutError(`${message}. Your section changes are not shared with the team yet.`);
      });
    return () => {
      cancelled = true;
    };
  }, [layoutLoaded, canEditBoard, projectId, viewerUserId, columnDefs, itemOrder]);

  useEffect(() => {
    saveProjectBoardCollapsed(projectId, collapsedIds);
  }, [projectId, collapsedIds]);

  /**
   * A task can name a section this board does not have — it was created before the
   * layout was shared, or someone deleted the section while this page was open.
   * Adopt the section rather than dropping the card, which is how 30-odd tasks used
   * to go missing. Titles come from this browser's old layout when it has them.
   */
  useEffect(() => {
    if (!layoutLoaded || tasksLoading) return;
    const known = new Set(columnDefs.map((c) => c.id));
    const orphanIds: string[] = [];
    for (const task of tasks) {
      const columnId = task.project_board_column_id;
      if (columnId && !known.has(columnId) && !orphanIds.includes(columnId)) orphanIds.push(columnId);
    }
    if (orphanIds.length === 0) return;
    const legacyTitles = new Map(
      (loadProjectBoardLayoutLegacy(projectId)?.columns ?? []).map((c) => [c.id, c.title])
    );
    setColumnDefs((prev) => [
      ...prev,
      ...orphanIds.map((id, idx) => ({
        id,
        title: legacyTitles.get(id) ?? `Section ${prev.length + idx + 1}`,
      })),
    ]);
  }, [layoutLoaded, tasksLoading, tasks, columnDefs, projectId]);

  useEffect(() => {
    if (editingColumnId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingColumnId]);

  const persistColumnMove = useCallback(
    (taskId: number, toColumnId: string) => {
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, project_board_column_id: toColumnId } : t))
      );
      updateTask(taskId, { project_board_column_id: toColumnId, acting_user_id: viewerUserId }).catch(() => {
        reloadTasks();
      });
    },
    [viewerUserId, reloadTasks]
  );

  const removeTaskFromProject = useCallback(
    async (task: TaskRow) => {
      if (!canEditBoard) return;
      const ok = window.confirm(
        `Remove "${task.name}" from this project?\n\nThe task will remain in your task list but will no longer appear on this project board.`
      );
      if (!ok) return;
      setRemovingTaskId(task.id);
      try {
        await updateTask(task.id, { project_id: null, acting_user_id: viewerUserId });
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
      } catch {
        window.alert('Could not remove this task from the project. You may not have permission.');
        reloadTasks();
      } finally {
        setRemovingTaskId(null);
      }
    },
    [canEditBoard, viewerUserId, reloadTasks]
  );

  const isCollapsed = useCallback((columnId: string) => collapsedIds.includes(columnId), [collapsedIds]);

  const toggleCollapse = useCallback((columnId: string) => {
    setCollapsedIds((prev) => (prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]));
  }, []);

  const startEditTitle = useCallback((col: ProjectBoardColumn) => {
    setEditingColumnId(col.id);
    setEditTitleDraft(col.title);
  }, []);

  const commitEditTitle = useCallback(
    (columnId: string) => {
      const next = editTitleDraft.trim();
      if (next) {
        setColumnDefs((prev) => prev.map((c) => (c.id === columnId ? { ...c, title: next } : c)));
      }
      setEditingColumnId(null);
      setEditTitleDraft('');
    },
    [editTitleDraft]
  );

  const cancelEditTitle = useCallback(() => {
    setEditingColumnId(null);
    setEditTitleDraft('');
  }, []);

  const handleDeleteColumn = useCallback(
    async (columnId: string) => {
      if (!canEditBoard) return;
      if (columnDefs.length <= 1) {
        window.alert('The board needs at least one section.');
        return;
      }
      const idx = columnDefs.findIndex((c) => c.id === columnId);
      if (idx < 0) return;
      const deletedTitle = columnDefs[idx].title;
      const targetCol = idx > 0 ? columnDefs[idx - 1] : columnDefs[idx + 1];
      if (!targetCol) return;
      const toMove = tasks.filter((t) => t.project_board_column_id === columnId);
      const msg =
        toMove.length > 0
          ? `Delete “${deletedTitle}”? ${toMove.length} task(s) will move to “${targetCol.title}”.`
          : `Delete section “${deletedTitle}”?`;
      if (!window.confirm(msg)) return;

      setDeletingColumnId(columnId);
      try {
        await Promise.all(
          toMove.map((t) =>
            updateTask(t.id, {
              project_board_column_id: targetCol.id,
              acting_user_id: viewerUserId,
            })
          )
        );
        setColumnDefs((prev) => prev.filter((c) => c.id !== columnId));
        setCollapsedIds((prev) => prev.filter((id) => id !== columnId));
        setTasks((prev) =>
          prev.map((t) =>
            t.project_board_column_id === columnId ? { ...t, project_board_column_id: targetCol.id } : t
          )
        );
        setEditingColumnId(null);
        setEditTitleDraft('');
      } catch {
        window.alert('Could not delete the section. Try again.');
        void reloadTasks();
      } finally {
        setDeletingColumnId(null);
      }
    },
    [canEditBoard, columnDefs, tasks, viewerUserId, reloadTasks]
  );

  const handleDragEnd = useCallback(() => {
    draggedRef.current = null;
    dragKindRef.current = null;
    draggedColumnIdRef.current = null;
    setDragged(null);
    setDragOverColumnId(null);
    setDragOverItemId(null);
    setDraggedColumnId(null);
    setColumnDragOverId(null);
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 80);
  }, []);

  const handleDragStart = useCallback(
    (e: React.DragEvent, itemId: string, fromColumnId: string) => {
      if (!canEditBoard) return;
      isDraggingRef.current = true;
      dragKindRef.current = 'card';
      const payload = { itemId, fromColumnId };
      draggedRef.current = payload;
      setDragged(payload);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', itemId);
    },
    [canEditBoard]
  );

  // ── Column (section) drag-to-reorder ──
  const handleColumnHeaderDragStart = useCallback(
    (e: React.DragEvent, columnId: string) => {
      if (!canEditBoard) return;
      isDraggingRef.current = true;
      dragKindRef.current = 'column';
      draggedColumnIdRef.current = columnId;
      setDraggedColumnId(columnId);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', `col:${columnId}`);
    },
    [canEditBoard]
  );

  const handleColumnReorderDragOver = useCallback(
    (e: React.DragEvent, columnId: string) => {
      if (!canEditBoard || dragKindRef.current !== 'column') return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const after = e.clientX > rect.left + rect.width / 2;
      columnInsertAfterRef.current = after;
      setColumnInsertAfter(after);
      setColumnDragOverId(columnId);
    },
    [canEditBoard]
  );

  const handleColumnReorderDrop = useCallback(
    (e: React.DragEvent, targetColumnId: string) => {
      if (dragKindRef.current !== 'column') return;
      e.preventDefault();
      e.stopPropagation();
      const sourceId = draggedColumnIdRef.current;
      setColumnDragOverId(null);
      if (!canEditBoard || !sourceId || sourceId === targetColumnId) {
        handleDragEnd();
        return;
      }
      const after = columnInsertAfterRef.current;
      setColumnDefs((prev) => {
        const fromIdx = prev.findIndex((c) => c.id === sourceId);
        const targetIdx = prev.findIndex((c) => c.id === targetColumnId);
        if (fromIdx < 0 || targetIdx < 0) return prev;
        const next = [...prev];
        const [moved] = next.splice(fromIdx, 1);
        let insertIdx = next.findIndex((c) => c.id === targetColumnId);
        if (insertIdx < 0) return prev;
        if (after) insertIdx += 1;
        next.splice(insertIdx, 0, moved);
        return next;
      });
      handleDragEnd();
    },
    [canEditBoard, handleDragEnd]
  );

  const handleColumnDragOver = useCallback(
    (e: React.DragEvent, columnId: string) => {
      if (!canEditBoard || dragKindRef.current === 'column') return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverColumnId(columnId);
      setDragOverItemId(null);
    },
    [canEditBoard]
  );

  const handleColumnDragLeave = useCallback((e: React.DragEvent) => {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      setDragOverColumnId(null);
    }
  }, []);

  const handleCardDragOver = useCallback(
    (e: React.DragEvent, columnId: string, cardId: string) => {
      if (!canEditBoard || dragKindRef.current === 'column') return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setDragOverColumnId(columnId);
      setDragOverItemId(cardId);
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const after = e.clientY > rect.top + rect.height / 2;
      dragInsertAfterRef.current = after;
      setDragInsertAfter(after);
    },
    [canEditBoard]
  );

  /** Reorder/move the dragged card into `toColumnId` at the position of `targetItemId` (or end). */
  const applyCardDrop = useCallback(
    (toColumnId: string, targetItemId: string | null) => {
      const d = draggedRef.current;
      if (!d) return;
      const { itemId: draggedId, fromColumnId } = d;
      const taskId = parseInt(draggedId, 10);
      if (!Number.isInteger(taskId)) return;

      const destCol = columns.find((c) => c.id === toColumnId);
      let destIds = (destCol ? destCol.items.map((i) => String(i.id)) : []).filter((id) => id !== draggedId);

      let insertIndex = destIds.length;
      if (targetItemId && targetItemId !== draggedId) {
        const ti = destIds.indexOf(targetItemId);
        if (ti >= 0) insertIndex = dragInsertAfterRef.current ? ti + 1 : ti;
      }
      destIds.splice(insertIndex, 0, draggedId);

      const movedColumns = fromColumnId !== toColumnId;
      let srcIds: string[] | null = null;
      if (movedColumns) {
        const srcCol = columns.find((c) => c.id === fromColumnId);
        srcIds = (srcCol ? srcCol.items.map((i) => String(i.id)) : []).filter((id) => id !== draggedId);
      }

      setItemOrder((prev) => {
        const next: ItemOrder = { ...prev, [toColumnId]: destIds.map(Number) };
        if (movedColumns && srcIds) next[fromColumnId] = srcIds.map(Number);
        return next;
      });

      if (movedColumns) {
        persistColumnMove(taskId, toColumnId);
      }
    },
    [columns, persistColumnMove]
  );

  const handleColumnDrop = useCallback(
    (e: React.DragEvent, columnId: string) => {
      if (dragKindRef.current === 'column') {
        handleColumnReorderDrop(e, columnId);
        return;
      }
      e.preventDefault();
      setDragOverColumnId(null);
      setDragOverItemId(null);
      if (!canEditBoard) {
        handleDragEnd();
        return;
      }
      applyCardDrop(columnId, null);
      handleDragEnd();
    },
    [handleDragEnd, canEditBoard, applyCardDrop, handleColumnReorderDrop]
  );

  const handleCardDrop = useCallback(
    (e: React.DragEvent, columnId: string, targetItemId: string) => {
      if (dragKindRef.current === 'column') return;
      e.preventDefault();
      e.stopPropagation();
      setDragOverColumnId(null);
      setDragOverItemId(null);
      if (!canEditBoard) {
        handleDragEnd();
        return;
      }
      applyCardDrop(columnId, targetItemId);
      handleDragEnd();
    },
    [handleDragEnd, canEditBoard, applyCardDrop]
  );

  const addSection = useCallback(() => {
    setColumnDefs((prev) => [...prev, { id: newId(), title: 'Untitled' }]);
  }, []);

  const openAddTask = useCallback(
    (columnId: string) => {
      if (!canEditBoard) return;
      setAddTaskColumnId(columnId);
    },
    [canEditBoard]
  );

  const renderColumnBody = (col: ProjectBoardColumn) => (
    <div
      className="project-board-column-body"
      onDragOver={(e) => handleColumnDragOver(e, col.id)}
      onDrop={(e) => handleColumnDrop(e, col.id)}
    >
      {col.items.length === 0 ? (
        <p className="project-board-empty-hint">No items yet. Drop cards here.</p>
      ) : null}
      {col.items.map((task) => {
        const itemId = String(task.id);
        const isDragging = dragged?.itemId === itemId;
        const insertBefore = dragOverItemId === itemId && !dragInsertAfter;
        const insertAfter = dragOverItemId === itemId && dragInsertAfter;
        const assignee = assigneeDisplay(task);
        return (
          <div
            key={task.id}
            role="button"
            tabIndex={0}
            draggable={canEditBoard}
            className={[
              'clickable-item',
              'tasks-board-item',
              canEditBoard ? 'tasks-board-item--draggable' : '',
              isDragging ? 'tasks-board-item--dragging' : '',
              insertBefore ? 'tasks-board-item--insert-before' : '',
              insertAfter ? 'tasks-board-item--insert-after' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onDragStart={(e) => handleDragStart(e, itemId, col.id)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleCardDragOver(e, col.id, itemId)}
            onDrop={(e) => handleCardDrop(e, col.id, itemId)}
            onClick={() => {
              if (!isDraggingRef.current) {
                navigate(`/tasks/${task.id}`, { state: { from: location.pathname + location.search } });
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/tasks/${task.id}`, { state: { from: location.pathname + location.search } });
              }
            }}
          >
            {canEditBoard ? (
              <div className="project-board-card-head">
                <span className="tasks-item-drag-handle" aria-hidden="true">
                  ⠿
                </span>
                <div className="project-board-card-title-wrap">
                  <span className="tasks-item-name project-board-card-name">
                    {task.name}
                    {task.priority ? (
                      <span
                        className="tasks-tag-pill"
                        style={{ backgroundColor: PRIORITY_PILL_COLORS[task.priority] ?? '#e5e7eb' }}
                      >
                        {task.priority}
                      </span>
                    ) : null}
                  </span>
                </div>
                <button
                  type="button"
                  className="project-board-remove-from-project-btn"
                  title="Remove from project"
                  aria-label={`Remove ${task.name} from this project`}
                  disabled={removingTaskId === task.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    void removeTaskFromProject(task);
                  }}
                >
                  <FontAwesomeIcon icon={faLinkSlash} />
                </button>
              </div>
            ) : null}
            <div
              className={
                canEditBoard ? 'tasks-item-body project-board-card-body-tail' : 'tasks-item-body'
              }
            >
              {!canEditBoard ? (
                <span className="tasks-item-name">
                  {task.name}
                  {task.priority ? (
                    <span
                      className="tasks-tag-pill"
                      style={{ backgroundColor: PRIORITY_PILL_COLORS[task.priority] ?? '#e5e7eb' }}
                    >
                      {task.priority}
                    </span>
                  ) : null}
                </span>
              ) : null}
              {task.tags && task.tags.length > 0 ? (
                <span className="tasks-item-tags">
                  {task.tags.map((tag) => (
                    <span
                      key={tag}
                      className="tasks-tag-pill"
                      style={{ backgroundColor: TAG_PILL_COLORS[tag] ?? '#8A8F93' }}
                    >
                      {TAG_LABELS[tag] ?? tag.replace(/_/g, ' ')}
                    </span>
                  ))}
                </span>
              ) : null}
              <span
                className={assignee ? 'tasks-item-assignee' : 'tasks-item-assignee tasks-item-assignee--unassigned'}
              >
                {assignee || 'Unassigned'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (!Number.isInteger(viewerUserId) || viewerUserId < 1) {
    return <p className="project-board-viewer-hint">Sign in to view and edit this board.</p>;
  }

  return (
    <div className="project-board-outer">
      {tasksError ? (
        <p className="create-order-error" role="alert">
          {tasksError}
        </p>
      ) : null}
      {layoutError ? (
        <p className="create-order-error" role="alert">
          {layoutError}
        </p>
      ) : null}
      {tasksLoading ? <p className="project-board-loading">Loading tasks…</p> : null}
      <div className="project-board-row">
        {columns.map((col) => {
          const collapsed = isCollapsed(col.id);
          const dragOver = dragOverColumnId === col.id && !dragOverItemId;

          if (collapsed) {
            const collapsedClasses = [
              'project-board-column',
              'project-board-column--collapsed',
              dragOver ? 'project-board-column--drag-over-collapsed' : '',
              draggedColumnId === col.id ? 'project-board-column--dragging' : '',
              columnDragOverId === col.id && !columnInsertAfter ? 'project-board-column--insert-before' : '',
              columnDragOverId === col.id && columnInsertAfter ? 'project-board-column--insert-after' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div
                key={col.id}
                className={collapsedClasses}
                onDragLeave={handleColumnDragLeave}
                onDragOver={(e) => handleColumnReorderDragOver(e, col.id)}
                onDrop={(e) => handleColumnReorderDrop(e, col.id)}
              >
                {canEditBoard ? (
                  <span
                    className="project-board-column-drag-handle project-board-column-drag-handle--collapsed"
                    role="button"
                    tabIndex={-1}
                    aria-label={`Drag to reorder ${col.title}`}
                    title="Drag to reorder section"
                    draggable
                    onDragStart={(e) => handleColumnHeaderDragStart(e, col.id)}
                    onDragEnd={handleDragEnd}
                  >
                    ⠿
                  </span>
                ) : null}
                <button
                  type="button"
                  className="project-board-collapsed-expand"
                  onClick={() => toggleCollapse(col.id)}
                  aria-label={`Expand ${col.title}`}
                  title="Expand section"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
                <div
                  className="project-board-collapsed-rail"
                  onDragOver={(e) => handleColumnDragOver(e, col.id)}
                  onDrop={(e) => handleColumnDrop(e, col.id)}
                >
                  <span className="project-board-collapsed-title">{col.title}</span>
                  <span className="project-board-collapsed-count" aria-label={`${col.items.length} items`}>
                    {col.items.length}
                  </span>
                </div>
              </div>
            );
          }

          const colClasses = [
            'project-board-column',
            dragOver ? 'project-board-column--drag-over' : '',
            draggedColumnId === col.id ? 'project-board-column--dragging' : '',
            columnDragOverId === col.id && !columnInsertAfter ? 'project-board-column--insert-before' : '',
            columnDragOverId === col.id && columnInsertAfter ? 'project-board-column--insert-after' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              key={col.id}
              className={colClasses}
              onDragLeave={handleColumnDragLeave}
              onDragOver={(e) => handleColumnReorderDragOver(e, col.id)}
              onDrop={(e) => handleColumnReorderDrop(e, col.id)}
            >
              <div className="project-board-column-header">
                <div className="project-board-header-title-row">
                  {canEditBoard ? (
                    <span
                      className="project-board-column-drag-handle"
                      role="button"
                      tabIndex={-1}
                      aria-label={`Drag to reorder ${col.title}`}
                      title="Drag to reorder section"
                      draggable
                      onDragStart={(e) => handleColumnHeaderDragStart(e, col.id)}
                      onDragEnd={handleDragEnd}
                    >
                      ⠿
                    </span>
                  ) : null}
                  <div className="project-board-header-title-block">
                    {editingColumnId === col.id ? (
                      <div className="project-board-title-edit-stack">
                        <input
                          ref={editInputRef}
                          type="text"
                          className="form-input project-board-title-input"
                          value={editTitleDraft}
                          aria-label="Section name"
                          disabled={deletingColumnId === col.id}
                          onChange={(e) => setEditTitleDraft(e.target.value)}
                          onBlur={() => commitEditTitle(col.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              commitEditTitle(col.id);
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditTitle();
                            }
                          }}
                        />
                        {canEditBoard && columnDefs.length > 1 ? (
                          <button
                            type="button"
                            className="project-board-delete-section-btn"
                            disabled={deletingColumnId === col.id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => void handleDeleteColumn(col.id)}
                          >
                            {deletingColumnId === col.id ? 'Deleting…' : 'Delete section'}
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <h3 className="project-board-column-title">
                        {col.title}{' '}
                        <span className="project-board-column-count" aria-label={`${col.items.length} items`}>
                          ({col.items.length})
                        </span>
                      </h3>
                    )}
                  </div>
                  <div className="project-board-header-actions">
                    <button
                      type="button"
                      className="project-board-header-icon-btn"
                      aria-label="Add task"
                      title="Add task"
                      disabled={!canEditBoard}
                      onClick={() => openAddTask(col.id)}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                    <button
                      type="button"
                      className="project-board-header-icon-btn"
                      aria-label="Collapse section"
                      title="Collapse section"
                      onClick={() => toggleCollapse(col.id)}
                    >
                      <FontAwesomeIcon icon={faAnglesLeft} />
                    </button>
                    <button
                      type="button"
                      className="project-board-header-icon-btn"
                      aria-label="Edit section name"
                      title="Edit section name"
                      disabled={editingColumnId === col.id}
                      onClick={() => startEditTitle(col)}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                  </div>
                </div>
              </div>
              {renderColumnBody(col)}
              <button
                type="button"
                className="project-board-add-task"
                disabled={!canEditBoard}
                onClick={() => openAddTask(col.id)}
              >
                Add task
              </button>
            </div>
          );
        })}
        <button type="button" className="project-board-add-section" onClick={addSection}>
          Add section
        </button>
      </div>

      <Modal
        isOpen={addTaskColumnId != null}
        onClose={() => setAddTaskColumnId(null)}
        title="Add New Task"
        wide
      >
        {addTaskColumnId != null ? (
          <AddTaskForm
            onClose={() => setAddTaskColumnId(null)}
            projectBoardContext={{
              projectId,
              boardColumnId: addTaskColumnId,
              ...(eventId != null ? { eventId } : {}),
            }}
            onCreated={(task) => {
              setTasks((prev) => [task, ...prev]);
              setAddTaskColumnId(null);
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default ProjectBoardTab;
